import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'

import { getGetMeQueryOptions, useGetMe } from '../../api/generated/endpoints/me/me'
import { ADMIN_ME_OPTIONS } from '../../lib/admin'
import { supabase } from '../../lib/supabase'

/**
 * The admin area: its own chrome, and the check that you belong in it.
 *
 * Unlike the `_authed` guard above this one, this cannot be synchronous. Admin
 * status is not in the session token — the backend derives it from the verified
 * email against the `admins` table and ADMIN_EMAILS — so we have to ask.
 *
 * Security is the backend's job; `require_admin` returns 403 regardless of what
 * happens here. This guard exists purely so nobody is confused.
 */
export const Route = createFileRoute('/_authed/_admin')({
  beforeLoad: async ({ context }) => {
    // Warm the cache so the shell does not flash "one moment" on the way in.
    // Failures are swallowed deliberately: a flaky network must not throw a
    // 91-year-old into a router error boundary. The component handles it.
    await context.queryClient
      .ensureQueryData(getGetMeQueryOptions(ADMIN_ME_OPTIONS))
      .catch(() => undefined)
  },
  component: AdminLayout,
})

function AdminLayout() {
  const me = useGetMe(ADMIN_ME_OPTIONS)

  if (me.isPending) return <Splash title="Un momento…" />

  if (me.isError) {
    return (
      <Splash title="No se pudo conectar con el sistema.">
        <button
          type="button"
          onClick={() => void me.refetch()}
          className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Reintentar
        </button>
      </Splash>
    )
  }

  if (!me.data.is_admin) return <NotAnAdmin email={me.data.email} />

  return (
    // A darker ground than the customer app, so white cards separate harder --
    // which genuinely helps aging contrast sensitivity. text-lg sets the base
    // size once here rather than per component.
    <div className="min-h-screen bg-slate-100 text-lg text-slate-900">
      <AdminHeader email={me.data.email} />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  )
}

/**
 * Where each tab leads, and which paths count as being "in" it. Tarifas is
 * reached from Ajustes and belongs to it; without this the header showed no
 * section at all while he was on it, which reads as being lost.
 */
const SECTIONS = [
  { to: '/admin/today', label: 'Hoy', also: ['/admin/booking', '/admin/new'] },
  { to: '/admin/calendar', label: 'Calendario', also: [] },
  { to: '/admin/customers', label: 'Clientes', also: [] },
  { to: '/admin/tournaments', label: 'Torneos', also: [] },
  { to: '/admin/closures', label: 'Días cerrados', also: [] },
  { to: '/admin/payments', label: 'Pagos', also: [] },
  { to: '/admin/reports', label: 'Números', also: [] },
  { to: '/admin/activity', label: 'Movimientos', also: [] },
  { to: '/admin/settings', label: 'Ajustes', also: ['/admin/rates'] },
] as const

function AdminHeader({ email }: { email: string | null }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const navigate = useNavigate()

  // It used to be a link to "/", which left the session alive: on a shared
  // iPad the next person to open the admin was still him.
  async function signOut() {
    await supabase.auth.signOut()
    void navigate({ to: '/', replace: true })
  }

  return (
    // Inverted against the customer app's white header: unmistakable at a
    // glance from across the counter. One row, not three -- the header was a
    // quarter of the iPad before anything on the page began.
    <header className="bg-slate-900 text-white">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 px-6 md:flex-nowrap">
        <span className="order-1 py-4 text-lg font-semibold whitespace-nowrap">
          Golf Bay Manager
        </span>

        <div className="order-2 ml-auto flex min-w-0 items-center gap-3 py-3 md:order-3">
          {/* The address used to sit here, and pushed the last tab off the
              row. It only matters when the wrong person is signed in, so it
              lives on the button that fixes that. */}
          <button
            type="button"
            onClick={() => void signOut()}
            title={email ? `Sesión de ${email}` : undefined}
            className="min-h-11 rounded-md border border-slate-600 px-4 text-base text-slate-200 hover:border-slate-400 hover:text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Salir
          </button>
        </div>

        {/* A short flat list of whole screens. This IS the navigation -- no
            menus, no nesting, nothing that only appears on hover.

            Sentence case and a bar under the current one, rather than eight
            all-caps pills: capitals are slower to read, and a pill per entry
            made every tab look equally pressed. On a narrow screen it scrolls
            sideways edge to edge, the same trick DayStrip uses. */}
        <nav
          aria-label="Secciones"
          className="order-3 -mx-6 flex w-[calc(100%+3rem)] gap-1 overflow-x-auto px-4 md:order-2 md:mx-0 md:w-auto md:px-0"
        >
          {SECTIONS.map((section) => {
            const current = [section.to, ...section.also].some(
              (path) => pathname === path || pathname.startsWith(`${path}/`),
            )
            return (
              <Link
                key={section.to}
                to={section.to}
                aria-current={current ? 'page' : undefined}
                className={[
                  'relative flex min-h-14 shrink-0 items-center px-2.5 text-base whitespace-nowrap focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-white',
                  // The one accent in the admin: fairway green, on the tab he
                  // is in. Nothing else uses it, so it only ever means "here".
                  current
                    ? 'font-semibold text-white after:absolute after:inset-x-2.5 after:bottom-0 after:h-1 after:rounded-t after:bg-emerald-500'
                    : 'text-slate-300 hover:text-white',
                ].join(' ')}
              >
                {section.label}
              </Link>
            )
          })}
        </nav>
      </div>
    </header>
  )
}

function Splash({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-lg">
      <h1 className="text-3xl font-semibold">{title}</h1>
      {children && <div className="mt-8">{children}</div>}
    </div>
  )
}

function NotAnAdmin({ email }: { email: string | null }) {
  // A plain page rather than a redirect. The realistic person who lands here is
  // the coach signed in with the wrong email address; bouncing him to the
  // customer landing page would leave him with no idea what happened.
  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-lg">
      <h1 className="text-3xl font-semibold">Esta parte es para el local.</h1>
      <p className="mt-4 text-slate-700">
        La sesión está iniciada como{' '}
        <span className="font-medium">{email ?? 'una cuenta desconocida'}</span>, que no está
        habilitada para gestionar el calendario.
      </p>
      <p className="mt-2 text-slate-700">
        Si no es la dirección correcta, hay que iniciar sesión con la otra.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void supabase.auth.signOut()}
          className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Iniciar sesión con otro correo
        </button>
        <Link
          to="/"
          className="inline-flex min-h-16 items-center rounded-md border-2 border-slate-400 px-8 text-xl"
        >
          Ir al sitio principal
        </Link>
      </div>
    </div>
  )
}
